/**
 * Recherche du header — le panneau de résultats, et rien d'autre.
 *
 * Porté de preview/kb-react/src/components/HeaderUX.jsx (panneau du bureau
 * lignes 296-337, liste du tiroir lignes 386-410).
 *
 * Le formulaire rendu par snippets/search-form.liquid est un
 * <form method="get"> vers /search : sans ce fichier, taper puis valider mène
 * à la page de résultats, comme n'importe quel formulaire. Le panneau est une
 * amélioration, jamais un passage obligé.
 *
 * ── Les choix qui ne sont pas évidents ──────────────────────────────────
 *
 * 1. Le panneau n'est plus dans le formulaire. Il descend sur toute la
 *    largeur de la barre, donc il est rendu une seule fois par
 *    sections/header.liquid au niveau de .hdr ; le champ le désigne par
 *    data-rp-panneau-id, et l'ARIA le relie par aria-controls.
 *
 * 2. Deux variantes de panneau, une seule requête. Le bureau reçoit la
 *    grille pleine largeur ; le tiroir reçoit une liste compacte qui prend
 *    la place du corps du menu (data-rp-remplace dit quel élément s'efface).
 *    La réponse de la section porte les deux dans des gabarits inertes : on
 *    clone celui qui convient, l'autre ne charge aucune image.
 *
 * 3. Les attributs ARIA du combobox sont posés ICI, pas dans le Liquid.
 *    Annoncer role="combobox" et aria-expanded="false" dans le HTML servi
 *    promettrait à un lecteur d'écran une liste qui ne s'ouvrira jamais si
 *    le module ne charge pas. On arme donc les champs au chargement, et à
 *    chaque shopify:section:load — l'éditeur de thème remplace le HTML de la
 *    section du header à chaque modification de réglage, et le champ rendu
 *    à neuf arrive désarmé.
 *
 * 4. Tout le reste passe par la délégation sur `document`, pour la même
 *    raison : des écouteurs posés sur les éléments seraient décrochés par ce
 *    même remplacement, et le snippet est rendu deux fois (barre du bureau
 *    et tiroir de menu) sans que ce fichier ait à les dénombrer.
 *
 * 5. Une requête en cours est annulée dès que la frappe reprend
 *    (AbortController). Sans cela, la réponse de « mat » pouvait arriver
 *    après celle de « matelas » et réécrire le panneau avec le mauvais
 *    contenu — d'autant plus visible que la frappe est rapide.
 */

const DELAI = 250; /* ms — assez pour ne pas requêter à chaque touche, assez
                      court pour que la liste paraisse suivre la frappe */
const MINIMUM = 2; /* caractères — sous deux lettres, tout le catalogue matche */
const RESSOURCES = 'product'; /* le prototype ne propose que des produits */
const LIMITE = 10;

/* Le HTML déjà reçu pour un terme donné : revenir en arrière avec la touche
   Retour arrière ne redemande rien au serveur. La réponse portant les deux
   variantes, les deux champs se partagent la même entrée. Plafonné, sinon
   une longue session de frappe garderait tout le catalogue en mémoire. */
const MEMOIRE = new Map();
const MEMOIRE_MAX = 30;

let minuterie = 0;
let requete = null;
let champActif = null;
let indexActif = -1;

/* ── accès au trio champ / panneau / région d'annonce ─────────────────── */

const panneauDe = (champ) =>
  champ?.dataset.rpPanneauId ? document.getElementById(champ.dataset.rpPanneauId) : null;

/* L'élément que la liste du tiroir efface le temps d'une recherche. */
const remplaceDe = (panneau) =>
  panneau?.dataset.rpRemplace ? document.getElementById(panneau.dataset.rpRemplace) : null;

const estTiroir = (panneau) => panneau?.dataset.rpVariante === 'tiroir';

const annonceurDe = (champ) => champ?.form?.querySelector('[data-rp-live]') ?? null;
const optionsDe = (panneau) => Array.from(panneau?.querySelectorAll('[role="option"]') ?? []);

/**
 * Pose les attributs du motif combobox sur un champ qui ne les a pas encore.
 * Idempotent : rappelée sur un champ déjà armé, elle ne fait rien.
 */
function armerChamp(champ) {
  if (champ.dataset.rpArme === 'oui') return;

  const panneau = panneauDe(champ);
  if (!panneau) return;

  champ.setAttribute('role', 'combobox');
  champ.setAttribute('aria-autocomplete', 'list');
  champ.setAttribute('aria-haspopup', 'listbox');
  champ.setAttribute('aria-expanded', 'false');
  champ.setAttribute('aria-controls', panneau.id);
  champ.dataset.rpArme = 'oui';
}

function armerChamps() {
  document.querySelectorAll('[data-rp-champ]').forEach(armerChamp);
}

/* ── ouverture, fermeture, sélection ──────────────────────────────────── */

function annoncer(champ, texte) {
  const annonceur = annonceurDe(champ);
  if (annonceur) annonceur.textContent = texte;
}

/**
 * Bureau : le panneau reste dans la mise en page et c'est `visibility` qui
 * le masque, donc la transition a un état de départ à animer. Un [hidden]
 * poserait display:none et le panneau apparaîtrait d'un coup.
 *
 * Tiroir : pas de transition à préserver, et deux corps de menu superposés
 * n'auraient aucun sens — la liste prend franchement la place de la
 * navigation, [hidden] convient.
 */
function ouvrirPanneau(champ) {
  const panneau = panneauDe(champ);
  if (!panneau) return;

  if (estTiroir(panneau)) {
    const remplace = remplaceDe(panneau);
    if (remplace) remplace.hidden = true;
    panneau.hidden = false;
  } else {
    panneau.classList.add('on');
    panneau.setAttribute('aria-hidden', 'false');
  }

  champ.setAttribute('aria-expanded', 'true');
  champActif = champ;
}

function fermerPanneau(champ, rendreLeFocus) {
  const cible = champ ?? champActif;
  if (!cible) return;

  const panneau = panneauDe(cible);
  if (panneau) {
    if (estTiroir(panneau)) {
      panneau.hidden = true;
      const remplace = remplaceDe(panneau);
      if (remplace) remplace.hidden = false;
    } else {
      panneau.classList.remove('on');
      panneau.setAttribute('aria-hidden', 'true');
    }
    panneau.removeAttribute('aria-busy');
  }

  cible.setAttribute('aria-expanded', 'false');
  cible.removeAttribute('aria-activedescendant');
  indexActif = -1;
  if (champActif === cible) champActif = null;
  if (rendreLeFocus) cible.focus();
}

function fermerTout() {
  document.querySelectorAll('[data-rp-champ]').forEach((champ) => fermerPanneau(champ, false));
}

/**
 * Déplace l'option active. Le focus ne bouge pas : il reste dans le champ,
 * et c'est aria-activedescendant qui dit au lecteur d'écran quelle option
 * est visée. C'est le motif combobox, pas une liste de liens tabulables.
 */
function activerOption(champ, index) {
  const panneau = panneauDe(champ);
  const options = optionsDe(panneau);
  if (options.length === 0) return;

  options.forEach((option) => option.setAttribute('aria-selected', 'false'));

  if (index < 0 || index >= options.length) {
    indexActif = -1;
    champ.removeAttribute('aria-activedescendant');
    return;
  }

  const option = options[index];
  option.setAttribute('aria-selected', 'true');
  champ.setAttribute('aria-activedescendant', option.id);
  option.scrollIntoView({ block: 'nearest' });
  indexActif = index;
}

/* ── requête ──────────────────────────────────────────────────────────── */

function adresse(champ, terme) {
  const parametres = new URLSearchParams({
    q: terme,
    'resources[type]': RESSOURCES,
    'resources[limit]': String(LIMITE),
    'resources[options][unavailable_products]': 'last',
    section_id: 'predictive-search',
  });
  /* La route vient du Liquid (data-rp-url) et non d'un chemin écrit ici :
     une boutique multilingue préfixe ses URL par la locale. */
  const racine = champ.form?.dataset.rpUrl || '/search/suggest';
  return `${racine}?${parametres}`;
}

/**
 * Injecte la variante qui convient au panneau, numérote les options — leurs
 * ids ne peuvent pas venir du Liquid : la même réponse sert les deux champs
 * de la page, et deux jeux d'ids identiques rendraient aria-activedescendant
 * ambigu — puis ouvre et annonce.
 */
function remplirPanneau(champ, html) {
  const panneau = panneauDe(champ);
  if (!panneau) return;

  const recu = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rp-in]');
  const gabarit = recu?.querySelector(estTiroir(panneau) ? '[data-rp-tiroir]' : '[data-rp-bureau]');
  if (!gabarit) {
    fermerPanneau(champ, false);
    return;
  }

  panneau.replaceChildren(gabarit.content.cloneNode(true));
  panneau.removeAttribute('aria-busy');

  optionsDe(panneau).forEach((option, rang) => {
    option.id = `${champ.id}-suggestion-${rang}`;
  });

  /* aria-controls doit désigner la liste elle-même, pas le cadre qui la
     contient : le panneau du bureau porte aussi sa ligne d'en-tête, et un
     role="listbox" posé dessus mentirait sur ce qu'il contient. La liste
     n'existe qu'une fois la réponse reçue, et le même HTML sert les deux
     champs : son id se numérote ici, comme celui des options. */
  const liste = panneau.querySelector('[role="listbox"]');
  if (liste) {
    liste.id = `${champ.id}-liste`;
    champ.setAttribute('aria-controls', liste.id);
  } else {
    champ.setAttribute('aria-controls', panneau.id);
  }

  indexActif = -1;
  champ.removeAttribute('aria-activedescendant');
  ouvrirPanneau(champ);
  annoncer(champ, recu.dataset.rpAnnonce ?? '');
}

function memoriser(terme, html) {
  if (MEMOIRE.size >= MEMOIRE_MAX) MEMOIRE.delete(MEMOIRE.keys().next().value);
  MEMOIRE.set(terme, html);
}

async function chercher(champ) {
  const terme = champ.value.trim();

  if (terme.length < MINIMUM) {
    fermerPanneau(champ, false);
    annoncer(champ, '');
    return;
  }

  if (MEMOIRE.has(terme)) {
    remplirPanneau(champ, MEMOIRE.get(terme));
    return;
  }

  requete?.abort();
  requete = new AbortController();

  const panneau = panneauDe(champ);
  if (panneau && champActif === champ) panneau.setAttribute('aria-busy', 'true');

  try {
    const reponse = await fetch(adresse(champ, terme), { signal: requete.signal });
    if (!reponse.ok) throw new Error(`Recherche prédictive : ${reponse.status}`);

    const html = await reponse.text();
    memoriser(terme, html);

    /* Le champ a pu changer pendant le trajet : on ne réécrit le panneau que
       si la réponse correspond encore à ce qui est affiché. */
    if (champ.value.trim() !== terme) return;
    remplirPanneau(champ, html);
  } catch (erreur) {
    if (erreur.name === 'AbortError') return;
    /* Une suggestion en moins n'empêche pas de chercher : on referme, le
       formulaire reste soumissible. */
    fermerPanneau(champ, false);
  }
}

/* ── événements, tous délégués sur document ───────────────────────────── */

document.addEventListener('input', (evenement) => {
  const champ = evenement.target.closest('[data-rp-champ]');
  if (!champ) return;

  armerChamp(champ);
  clearTimeout(minuterie);
  minuterie = setTimeout(() => chercher(champ), DELAI);
});

document.addEventListener('keydown', (evenement) => {
  const champ = evenement.target.closest('[data-rp-champ]');
  if (!champ) return;

  const panneau = panneauDe(champ);
  const ouvert = Boolean(panneau) && champActif === champ;
  const options = optionsDe(panneau);

  switch (evenement.key) {
    case 'ArrowDown':
      if (!ouvert || options.length === 0) return;
      evenement.preventDefault();
      activerOption(champ, indexActif + 1 >= options.length ? 0 : indexActif + 1);
      break;

    case 'ArrowUp':
      if (!ouvert || options.length === 0) return;
      evenement.preventDefault();
      activerOption(champ, indexActif - 1 < 0 ? options.length - 1 : indexActif - 1);
      break;

    case 'Enter':
      /* Sans option active, on laisse le formulaire partir : c'est la
         recherche complète, et c'est le comportement attendu. */
      if (!ouvert || indexActif < 0 || !options[indexActif]) return;
      evenement.preventDefault();
      window.location.assign(options[indexActif].href);
      break;

    case 'Escape':
      if (!ouvert) return;
      evenement.preventDefault();
      fermerPanneau(champ, true);
      break;

    default:
      break;
  }
});

/* Le survol à la souris et le parcours au clavier doivent désigner la même
   option : sans cela, cliquer après avoir flèché ouvrirait autre chose que
   ce qui est surligné. */
document.addEventListener('mouseover', (evenement) => {
  const option = evenement.target.closest('[role="option"]');
  if (!option || !champActif) return;

  const options = optionsDe(panneauDe(champActif));
  const rang = options.indexOf(option);
  if (rang >= 0 && rang !== indexActif) activerOption(champActif, rang);
});

document.addEventListener('click', (evenement) => {
  /* Un clic dans le formulaire ou dans le panneau ne ferme rien : la
     suggestion est un lien, la navigation s'en charge. Le panneau vit hors
     du formulaire depuis qu'il est pleine largeur, d'où les deux tests. */
  if (evenement.target.closest('[data-rp]')) return;
  if (evenement.target.closest('[data-rp-panneau]')) return;
  fermerTout();
});

/* Sortir du formulaire à la tabulation ferme aussi : relatedTarget dit où le
   focus va, ce que `blur` seul ne dit pas.
   relatedTarget nul veut dire « le focus ne va nulle part » — c'est ce que
   renvoie Safari quand on clique un lien, qu'il ne focalise pas. Fermer là
   escamoterait la suggestion entre le mousedown et le click, et le clic ne
   toucherait plus rien. La fermeture au clic extérieur s'en charge déjà. */
document.addEventListener('focusout', (evenement) => {
  const formulaire = evenement.target.closest('[data-rp]');
  if (!formulaire) return;
  if (!evenement.relatedTarget) return;
  if (formulaire.contains(evenement.relatedTarget)) return;
  if (evenement.relatedTarget.closest?.('[data-rp-panneau]')) return;

  const champ = formulaire.querySelector('[data-rp-champ]');
  if (champ) fermerPanneau(champ, false);
});

/* Revenir dans un champ déjà rempli rouvre la liste sans reposer de question
   au serveur, puisque le terme est en mémoire. */
document.addEventListener('focusin', (evenement) => {
  const champ = evenement.target.closest('[data-rp-champ]');
  if (!champ) return;

  armerChamp(champ);
  const panneau = panneauDe(champ);
  if (panneau && panneau.firstElementChild && champ.value.trim().length >= MINIMUM) {
    ouvrirPanneau(champ);
  }
});

/**
 * L'éditeur de thème vient de remplacer le HTML d'une section : les champs
 * qu'elle contient sont neufs et désarmés, et l'état gardé ici pointe sur des
 * éléments qui ne sont plus dans le document.
 */
document.addEventListener('shopify:section:load', () => {
  clearTimeout(minuterie);
  requete?.abort();
  champActif = null;
  indexActif = -1;
  armerChamps();
});

armerChamps();
